/**
 * Paths that own their chrome instead of the app sidebar. Toolfare rewrites
 * only its root to /connect; callers first normalize the browser root with
 * productInternalPath. All other paths, auth gates, and merchant routes stay
 * intact. MCP continues using the canonical legacy API identity in Stage 1.
 */

/** Path prefix the Connect route group lives under. */
export const CONNECT_PATH_PREFIX = '/connect'

/**
 * Marketing surfaces under `/connect` that own their chrome as a subtree.
 *
 * `/connect` itself is an exact match (below) because it has siblings that must
 * NOT be claimed. These entries are different: everything beneath them is a
 * public marketing page by construction, because they are directories in the
 * `(connect)` route group rather than paths that merely share a prefix.
 */
const CONNECT_CHROME_SUBTREES: readonly string[] = [
  '/connect/data',
  '/connect/agents',
]

/**
 * Does this path own its chrome — header, footer, no app sidebar?
 *
 * `/connect` is an exact match on purpose. It is also the prefix of the
 * merchant OAuth landings (`/connect/shopify`, `/connect/shoplazza`,
 * `/connect/shopline`), which live in `app/connect/` rather than the
 * `(connect)` route group, predate this surface, carry a bind code from a
 * marketplace, and must keep rendering whatever chrome they render today. A
 * blanket `startsWith('/connect')` would silently restyle three live
 * partner-install entry points, so the claim is enumerated instead: the exact
 * landing path, plus the subtrees listed above.
 *
 * What a wrong answer costs, because no build or type check can see it:
 * `AppSidebarShell` clips its content with `overflow-hidden`
 * (`app-sidebar-shell.tsx`, on both the inset and the content div). A landing
 * page is almost entirely below the fold, so wrapping it does not merely look
 * wrong — the page has no scroll path at all and most of it is unreachable.
 * That exact failure has now shipped three times: the `/authorize` consent
 * screen, whose Allow/Decline buttons ended up outside the viewport; the whole
 * Connect console; and `/connect/data/*`, which was added under this prefix
 * without anyone editing this function. All three were found by a person
 * looking at a screenshot, which is why `connect-host-routing.test.ts` now
 * derives the expected answer from the route directories instead of listing
 * paths by hand — adding a page under the route group is what must turn it
 * red, not remembering to add an assertion.
 *
 * Trailing slashes are not handled here and do not need to be: Next answers
 * `/connect/` with a redirect to `/connect` before any component runs.
 */
export function isConnectChromePath(pathname: string): boolean {
  if (pathname === CONNECT_PATH_PREFIX) return true
  return CONNECT_CHROME_SUBTREES.some(
    // `=== prefix` for the subtree root, `prefix + '/'` for its pages. A bare
    // `startsWith(prefix)` would also claim `/connect/database-anything`.
    subtree => pathname === subtree || pathname.startsWith(`${subtree}/`),
  )
}
